/* eslint-disable import/prefer-default-export */
import { useEffect, useRef, useState } from 'react';

import { getPreferencesStatuses } from 'PrivateAPI';

import { PreferenceStatus, SellerCollectionPreference } from '../CollectionPreferences/collectionPreferences.types';

type GetPreferencesStatusesResponse = {
  data: false | SellerCollectionPreference;
  status: string;
}

const resolveCollectionPreferenceStatus = (data: GetPreferencesStatusesResponse['data']): PreferenceStatus => {
  if (data === false || !data) {
    return PreferenceStatus.NOT_REQUESTED;
  }

  const { isExpired, isSubmitted } = data;

  if (isSubmitted || isExpired) {
    return PreferenceStatus.COMPLETED;
  }

  return PreferenceStatus.REQUESTED;
};

export type UseGetPreferencesStatuses = {
  datePreferencesState?: SellerCollectionPreference;
  error?: Error | null;
  expiryAt?: string;
  loading?: boolean;
  refetch?: () => void;
  refetchCount?: number;
  status: PreferenceStatus;
};

const useGetPreferencesStatuses = (dpEnquiryId: number | undefined): UseGetPreferencesStatuses => {
  const [loading, setLoading] = useState<boolean>(true);
  const [status, setStatus] = useState<PreferenceStatus>(PreferenceStatus.NOT_REQUESTED);
  const [datePreferencesState, setDatePreferencesState] = useState<SellerCollectionPreference>();
  const [expiryAt, setExpiryAt] = useState<string>();
  const [refetch, setRefetch] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const cancelled = useRef(false);

  useEffect(() => () => {
    cancelled.current = true;
  }, []);

  useEffect(() => {
    const getPreferenceStatuses = async () => {
      setLoading(true);

      try {
        if (!Number.isInteger(dpEnquiryId)) {
          throw new Error('Enquiry ID is invalid or not supplied.');
        }

        const { data } = await getPreferencesStatuses(dpEnquiryId) as GetPreferencesStatusesResponse;
        const resolvedStatus = resolveCollectionPreferenceStatus(data);

        if (cancelled?.current) {
          return;
        }

        if (data && data?.expiresAt) {
          setExpiryAt(data.expiresAt);
        }

        setStatus(resolvedStatus);
        setDatePreferencesState(data as SellerCollectionPreference);
      } catch (err) {
        if (cancelled?.current) {
          return;
        }
        setError(err as Error);
        window?.Sentry?.captureException?.(err);
      } finally {
        if (!cancelled?.current) {
          setLoading(false);
        }
      }
    };

    if (dpEnquiryId) {
      getPreferenceStatuses();
    }
  }, [dpEnquiryId, refetch]);

  return {
    datePreferencesState,
    error,
    expiryAt,
    loading,
    refetch: () => setRefetch(refetch + 1),
    refetchCount: refetch,
    status,
  };
};

export default useGetPreferencesStatuses;
